import { useState, useEffect } from "react";
import {
  Box,
  TextField,
  CircularProgress,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  InputAdornment,
  Button
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AppBreadcrumbs from "../../../components/AppBreadcrumbs";
import * as services from "../../../services/saleng";

export default function DailyPurchaseSummary() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [orderBy, setOrderBy] = useState("code");
  const [order, setOrder] = useState("asc");
  const [filterText, setFilterText] = useState("");

  const handleSort = (field) => {
    const isAsc = orderBy === field && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(field);
  };

  const cellStyle = { border: "1px solid #ddd" };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [resProducts, resInventory, resPackages] = await Promise.all([
          services.getProducts(),
          services.getInventory(),
          services.getPackages(),
        ]);

        const products = resProducts.data?.data ?? [];
        const inventories = resInventory.data?.data ?? [];
        const packages = resPackages ?? [];

        const inventoryMap = {};
        inventories.forEach((inv) => {
          inventoryMap[inv.productId] = inv;
        });

        const productMap = {};

        products.forEach((p) => {
          const inv = inventoryMap[p.id];
          const weight = inv?.quantity || 0;
          const averageCost = inv?.averageCost || 0;
          const amountBeforeVat = weight * averageCost;

          productMap[p.id] = {
            id: p.id,
            code: p.code,
            title: p.title,
            category: p.category?.name || "-",
            sub_category: p.subCategory?.name || "-",
            unit: "กก.",
            weight,
            pack_weight: 0,
            pack_amount: 0,
            average_cost: averageCost,
            amount_before_vat: amountBeforeVat,
          };
        });

        packages.forEach((pkg) => {
          const id = pkg.productId;
          const quantity = pkg.quantity || 0;
          if (!productMap[id]) return;
          productMap[id].pack_amount += 1;
          productMap[id].pack_weight += quantity;
        });

        const result = Object.values(productMap).map((item) => ({
          ...item,
          sub_weight: item.weight - item.pack_weight,
        }));

        setRows(result);
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatNum = (value) =>
    new Intl.NumberFormat("th-TH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value ?? 0);

  const getComparator = (order, orderBy) => {
    return order === "desc"
      ? (a, b) =>
        typeof b[orderBy] === "string"
          ? b[orderBy]?.localeCompare(a[orderBy])
          : (b[orderBy] ?? 0) - (a[orderBy] ?? 0)
      : (a, b) =>
        typeof a[orderBy] === "string"
          ? a[orderBy]?.localeCompare(b[orderBy])
          : (a[orderBy] ?? 0) - (b[orderBy] ?? 0);
  };

  const sortData = (data, comparator) => [...data].sort(comparator);

  const filteredRows = rows.filter((r) => {
    const text = filterText.toLowerCase();
    return (
      r.code?.toLowerCase().includes(text) ||
      r.title?.toLowerCase().includes(text) ||
      r.category?.toLowerCase().includes(text) ||
      r.sub_category?.toLowerCase().includes(text)
    );
  });

  const totals = filteredRows.reduce(
    (acc, curr) => {
      acc.weight += curr.weight || 0;
      acc.sub_weight += curr.sub_weight || 0;
      acc.pack_weight += curr.pack_weight || 0;
      acc.pack_amount += curr.pack_amount || 0;
      acc.amount_before_vat += curr.amount_before_vat || 0;
      acc.average_cost += curr.count ? curr.average_cost : 0;
      acc.count += curr.count || 0;
      return acc;
    },
    {
      weight: 0,
      sub_weight: 0,
      pack_weight: 0,
      pack_amount: 0,
      amount_before_vat: 0,
      average_cost: 0,
      count: 0,
    }
  );

  if (totals.weight > 0) {
    totals.average_cost = totals.amount_before_vat / totals.weight;
  }


  function exportToCSV(data, filename = "report.csv") {
    if (!data || data.length === 0) return;
    const headers = Object.keys(data[0]);
    const rows = data.map((row) =>
      headers.map((field) => `"${row[field] ?? ""}"`).join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  const handleExportCSV = () => {
    const dataToExport = sortData(filteredRows, getComparator(order, orderBy)).map(
      (row) => ({
        หมวดหมู่หลัก: row.category,
        หมวดหมู่ย่อย: row.sub_category,
        รหัส: row.code,
        สินค้า: row.title,
        น้ำหนักรวม: Number(row.weight ?? 0).toFixed(2),
        น้ำหนักย่อย: Number(row.sub_weight ?? 0).toFixed(2),
        น้ำหนักแพ็ค: Number(row.pack_weight ?? 0).toFixed(2),
        จำนวนแพ็ค: Number(row.pack_amount ?? 0),
        หน่วย: row.unit || "กก.",
        ต้นทุนเฉลี่ย: Number(row.average_cost ?? 0).toFixed(2),
        ยอดรวม: Number(row.amount_before_vat ?? 0).toFixed(2),
      })
    );

    exportToCSV(
      dataToExport,
      `cost_report_${new Date().toISOString().slice(0, 10)}.csv`
    );
  };


  return (
    <Box
      sx={{
        p: 2,
        width: "100%",
        backgroundColor: "#f9fafb",
        minHeight: "100vh",
      }}
    >
      <AppBreadcrumbs />

      <Box display="flex" justifyContent="flex-end" gap={2} my={2}>
        <TextField
          label="ค้นหา"
          variant="outlined"
          size="large"
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          sx={{ width: "400px" }}
          placeholder="ค้นหาด้วยรหัส ชื่อสินค้าหรือหมวดหมู่"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" my={1}>
            <Typography variant="h6">รายงานต้นทุนสินค้า</Typography>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={handleExportCSV}
              size="large"
            >
              ข้อมูลหลัก
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ backgroundColor: "#f0f0f0" }}>
                <TableRow>
                  <TableCell sx={{ ...cellStyle, color: "primary.main" }}>
                    <TableSortLabel
                      active={orderBy === "category"}
                      direction={orderBy === "category" ? order : "asc"}
                      onClick={() => handleSort("category")}
                    >
                      หมวดหมู่หลัก
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, color: "primary.main" }}>
                    <TableSortLabel
                      active={orderBy === "sub_category"}
                      direction={orderBy === "sub_category" ? order : "asc"}
                      onClick={() => handleSort("sub_category")}
                    >
                      หมวดหมู่ย่อย
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, color: "primary.main" }}>
                    <TableSortLabel
                      active={orderBy === "code"}
                      direction={orderBy === "code" ? order : "asc"}
                      onClick={() => handleSort("code")}
                    >
                      รหัส
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ ...cellStyle, color: "primary.main" }}>
                    <TableSortLabel
                      active={orderBy === "title"}
                      direction={orderBy === "title" ? order : "asc"}
                      onClick={() => handleSort("title")}
                    >
                      สินค้า
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...cellStyle, color: "primary.main" }}
                  >
                    <TableSortLabel
                      active={orderBy === "weight"}
                      direction={orderBy === "weight" ? order : "asc"}
                      onClick={() => handleSort("weight")}
                    >
                      น้ำหนักรวม
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...cellStyle, color: "primary.main" }}
                  >
                    <TableSortLabel
                      active={orderBy === "sub_weight"}
                      direction={orderBy === "sub_weight" ? order : "asc"}
                      onClick={() => handleSort("sub_weight")}
                    >
                      น้ำหนักย่อย
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    น้ำหนักแพ็ค
                  </TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    จำนวนแพ็ค
                  </TableCell>
                  <TableCell sx={cellStyle}>หน่วย</TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...cellStyle, color: "primary.main" }}
                  >
                    <TableSortLabel
                      active={orderBy === "average_cost"}
                      direction={orderBy === "average_cost" ? order : "asc"}
                      onClick={() => handleSort("average_cost")}
                    >
                      ต้นทุนเฉลี่ย
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ ...cellStyle, color: "primary.main" }}
                  >
                    <TableSortLabel
                      active={orderBy === "amount_before_vat"}
                      direction={
                        orderBy === "amount_before_vat" ? order : "asc"
                      }
                      onClick={() => handleSort("amount_before_vat")}
                    >
                      ยอดรวม
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortData(filteredRows, getComparator(order, orderBy)).map(
                  (row) => (
                    <TableRow key={row.id}>
                      <TableCell sx={cellStyle}>{row.category}</TableCell>
                      <TableCell sx={cellStyle}>{row.sub_category}</TableCell>
                      <TableCell sx={cellStyle}>{row.code}</TableCell>
                      <TableCell sx={cellStyle}>{row.title}</TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {formatNum(row.weight)}
                      </TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {formatNum(row.sub_weight)}
                      </TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {formatNum(row.pack_weight)}
                      </TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {row.pack_amount}
                      </TableCell>
                      <TableCell sx={cellStyle}>{row.unit}</TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {formatNum(row.average_cost)}
                      </TableCell>
                      <TableCell align="right" sx={cellStyle}>
                        {formatNum(row.amount_before_vat)}
                      </TableCell>
                    </TableRow>
                  )
                )}
                <TableRow
                  sx={{
                    backgroundColor: "#f5f5f5",
                    position: "sticky",
                    bottom: 0,
                  }}
                >
                  <TableCell colSpan={4} sx={cellStyle}>
                    <strong>รวมทั้งหมด</strong>
                  </TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    <strong>{formatNum(totals.weight)}</strong>
                  </TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    <strong>{formatNum(totals.sub_weight)}</strong>
                  </TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    <strong>{formatNum(totals.pack_weight)}</strong>
                  </TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    <strong>{totals.pack_amount}</strong>
                  </TableCell>
                  <TableCell sx={cellStyle}>กก.</TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    <strong>{formatNum(totals.average_cost)}</strong>
                  </TableCell>
                  <TableCell align="right" sx={cellStyle}>
                    <strong>{formatNum(totals.amount_before_vat)}</strong>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
